import { escapeHtml } from '../../../shared/utils/escapeHtml';
import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import type { AdsChartResponse } from '@brand24-original/llm-types';
import LineChart from '../../../shared/components/chart/line-chart/LineChart';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import FilterBox from '../../../shared/components/filter-box/FilterBox';
import { FilterItem } from '../../../shared/components/filter-box/models/FilterBoxModel';
import { buildBucketLabelFormatters } from '../../../shared/helpers/date/bucketDateLabels';
import { getChartThemeColors, getFirstSeriesPalette } from '../../../shared/utils/chartTheme';
import { SELECTED_BRAND_COLOR } from '../../../shared/utils/utils';
import { PeriodType } from '../../../shared/types/prompt/PeriodType';
import { buildAdsBucketKeys, zeroFillAdsBuckets } from '../helpers/AdsHelper';
import { AdsErrorPanel, ViewSwitch } from './AdsTableParts';

type ChartView = 'occurrences' | 'byAdvertiser';

/** Advertisers drawn in each bucket of the By-advertiser view (that bucket's top N by occurrences). */
const TOP_ADVERTISERS_PER_BUCKET = 15;

/** Chart views — a `.switch-box` next to the chart title (icon toggles went unnoticed). */
const VIEWS: { value: ChartView; labelKey: string; icon: string }[] = [
  { value: 'occurrences', labelKey: 'ads.viewOccurrences', icon: 'ico--activity' },
  { value: 'byAdvertiser', labelKey: 'ads.viewByAdvertiser', icon: 'ico--chart' },
];

interface AdPresenceSectionProps {
  chart: AdsChartResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  period: PeriodType;
  startDate: Date | null;
  endDate: Date | null;
  /** Daily / Weekly / Monthly links rendered in the panel header (like the Sources trends chart). */
  periodFilterItems: FilterItem[];
  /** Stable advertiser domain → color mapping shared with the Ad share table. */
  colorForAdvertiser: (domain: string) => string;
}

const AdPresenceSection: React.FC<AdPresenceSectionProps> = ({
  chart,
  isLoading,
  isError,
  onRetry,
  period,
  startDate,
  endDate,
  periodFilterItems,
  colorForAdvertiser,
}) => {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<ChartView>('occurrences');

  const labelFormatters = useMemo(
    () => buildBucketLabelFormatters(period, i18n.language),
    [period, i18n.language]
  );

  const unit =
    period === 'MONTHLY' ? t('ads.unitMonth') : period === 'WEEKLY' ? t('ads.unitWeek') : t('ads.unitDay');

  const bucketKeys = useMemo(
    () => (startDate && endDate ? buildAdsBucketKeys(startDate, endDate, period) : []),
    [startDate, endDate, period]
  );

  const buckets = useMemo(
    () => zeroFillAdsBuckets(bucketKeys, chart?.buckets ?? []),
    [bucketKeys, chart?.buckets]
  );

  const hasNoAds =
    !isLoading && !!chart && chart.advertiserBuckets.length === 0 &&
    buckets.every((bucket) => bucket.adOccurrences === 0);

  // Your brand vs Other brands — split on the advertiser domain (isYou = project domain match).
  const occurrencesMatrix = useMemo(() => {
    const yourByDate = new Map<string, number>();
    const otherByDate = new Map<string, number>();
    for (const row of chart?.advertiserBuckets ?? []) {
      const target = row.isYou ? yourByDate : otherByDate;
      target.set(row.date, (target.get(row.date) ?? 0) + row.occurrences);
    }
    const header: (string | number)[] = ['Date', t('ads.yourBrand'), t('ads.otherBrands')];
    const rows = bucketKeys.map((date) => [
      date,
      yourByDate.get(date) ?? 0,
      otherByDate.get(date) ?? 0,
    ]);
    return [header, ...rows];
  }, [chart?.advertiserBuckets, bucketKeys, t]);

  // LineChart's axis defaults are built for 1–30 position charts (min 1, max floored at 30) —
  // occurrence counts need an explicit 0-based axis scaled to the data.
  const occurrencesAxisMax = useMemo(() => {
    let max = 0;
    for (const row of occurrencesMatrix.slice(1)) {
      max = Math.max(max, Number(row[1]) || 0, Number(row[2]) || 0);
    }
    return Math.max(4, Math.ceil(max * 1.2));
  }, [occurrencesMatrix]);

  // Per bucket: that bucket's top N advertisers are stacked; everything below the cut is left out of
  // the bars (a big grey "Other" slice would hide the interesting part). The tooltip total and
  // the note under the chart still account for the ads that are not drawn.
  const byAdvertiser = useMemo(() => {
    const advertiserRows = chart?.advertiserBuckets ?? [];
    if (advertiserRows.length === 0 || bucketKeys.length === 0) {
      return null;
    }

    const dateIndex = new Map(bucketKeys.map((date, index) => [date, index]));
    const perBucket = bucketKeys.map(() => new Map<string, number>());
    const totals = new Map<string, number>();
    for (const row of advertiserRows) {
      const index = dateIndex.get(row.date);
      if (index === undefined) continue;
      perBucket[index].set(row.domain, (perBucket[index].get(row.domain) ?? 0) + row.occurrences);
      totals.set(row.domain, (totals.get(row.domain) ?? 0) + row.occurrences);
    }

    const shownPerBucket: Set<string>[] = [];
    const bucketTotals: number[] = [];
    const hiddenPerBucket: number[] = [];
    let hiddenAds = 0;
    perBucket.forEach((bucket, index) => {
      const ranked = Array.from(bucket.entries()).sort(
        (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
      );
      bucketTotals[index] = ranked.reduce((sum, [, occurrences]) => sum + occurrences, 0);
      shownPerBucket[index] = new Set(
        ranked.slice(0, TOP_ADVERTISERS_PER_BUCKET).map(([domain]) => domain)
      );
      hiddenPerBucket[index] = ranked
        .slice(TOP_ADVERTISERS_PER_BUCKET)
        .reduce((sum, [, occurrences]) => sum + occurrences, 0);
      hiddenAds += hiddenPerBucket[index];
    });

    // Series = union of the per-bucket tops, ordered by global occurrences (stable stacking order that
    // follows the ranking table's colors). Keyed by domain; displayed by brand name.
    const seriesDomains = Array.from(new Set(shownPerBucket.flatMap((set) => Array.from(set)))).sort(
      (a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0) || a.localeCompare(b)
    );
    const seriesData = new Map(
      seriesDomains.map((domain) => [
        domain,
        bucketKeys.map((_, index) =>
          shownPerBucket[index].has(domain) ? perBucket[index].get(domain) ?? 0 : 0
        ),
      ])
    );
    // ECharts series names must be unique — two advertisers sharing a display name keep the domain.
    const nameByDomain = new Map<string, string>();
    for (const row of advertiserRows) {
      if (!nameByDomain.has(row.domain) && row.advertiser) nameByDomain.set(row.domain, row.advertiser);
    }
    const usedLabels = new Set<string>();
    const labelByDomain = new Map(
      seriesDomains.map((domain) => {
        const preferred = nameByDomain.get(domain) ?? domain;
        const label = usedLabels.has(preferred) ? domain : preferred;
        usedLabels.add(label);
        return [domain, label];
      })
    );

    const themeColors = getChartThemeColors();
    const axisLabels = bucketKeys.map((date) => labelFormatters.axis(date));
    const tooltipLabels = bucketKeys.map((date) => labelFormatters.tooltip(date));
    const totalLabel = t('ads.tooltipTotal');
    const otherLabel = t('ads.otherAdvertisers');

    // No in-canvas legend (up to 15 series per bucket — it only added noise); the tooltip lists the
    // bucket's advertisers instead, and the ranking table on the right carries the colour dots.
    const option: EChartsOption = {
      // top clears the absolutely positioned view switch in the card corner.
      grid: { top: 40, bottom: 16, left: 16, right: 16, containLabel: true },
      xAxis: {
        type: 'category',
        data: axisLabels,
        axisLabel: {
          fontSize: 11,
          color: themeColors.axisLabelSecondary,
          rotate: bucketKeys.length > 14 ? 45 : 0,
          interval: 'auto',
          hideOverlap: true,
        },
        axisLine: { lineStyle: { color: themeColors.splitLine } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        // Occurrence counts are integers — no 0.5 ticks on sparse data.
        minInterval: 1,
        axisLabel: { fontSize: 11, color: themeColors.axisLabelSecondary },
        splitLine: { lineStyle: { color: themeColors.splitLine } },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        borderRadius: 8,
        padding: [8, 12],
        borderColor: themeColors.tooltipBorder,
        backgroundColor: themeColors.tooltipBg,
        textStyle: { fontSize: 12, color: themeColors.tooltipText },
        formatter: (params: unknown) => {
          const items = (Array.isArray(params) ? params : [params]) as {
            dataIndex: number;
            marker: string;
            seriesName: string;
            value: number;
          }[];
          const dataIndex = items.length > 0 ? items[0].dataIndex : -1;
          const headerLabel = dataIndex >= 0 ? tooltipLabels[dataIndex] ?? '' : '';
          const header = headerLabel
            ? `<div class="chart-tooltip__header">${escapeHtml(headerLabel)}</div>`
            : '';
          const rows = items
            .filter((item) => item.value > 0)
            .map(
              (item) =>
                `<div class="chart-tooltip__row">
                  <div class="chart-tooltip__marker">${item.marker}</div>
                  <div class="chart-tooltip__text">${escapeHtml(item.seriesName)}</div>
                  <div><strong>${item.value.toLocaleString()}</strong></div>
                </div>`
            )
            .join('');
          // Ads below the per-bucket cut are not drawn, but they are listed here (no marker — there
          // is no series for them) so that the rows add up to the total.
          const hidden = dataIndex >= 0 ? hiddenPerBucket[dataIndex] ?? 0 : 0;
          const otherRow =
            hidden > 0
              ? `<div class="chart-tooltip__row">
                  <div class="chart-tooltip__marker"></div>
                  <div class="chart-tooltip__text">${escapeHtml(otherLabel)}</div>
                  <div><strong>${hidden.toLocaleString()}</strong></div>
                </div>`
              : '';
          const total = dataIndex >= 0 ? bucketTotals[dataIndex] ?? 0 : 0;
          const totalRow = `<div class="chart-tooltip__row">
                  <div class="chart-tooltip__marker"></div>
                  <div class="chart-tooltip__text">${escapeHtml(totalLabel)}</div>
                  <div><strong>${total.toLocaleString()}</strong></div>
                </div>`;
          return `<div class="chart-tooltip__content">${header}${rows}${otherRow}${totalRow}</div>`;
        },
      },
      series: seriesDomains.map((domain) => ({
        name: labelByDomain.get(domain)!,
        type: 'bar',
        stack: 'total',
        data: seriesData.get(domain)!,
        barCategoryGap: '70%',
        itemStyle: { color: colorForAdvertiser(domain) },
        emphasis: { focus: 'none' },
      })),
    };

    return { option, hiddenAds };
  }, [chart?.advertiserBuckets, bucketKeys, labelFormatters, colorForAdvertiser, t]);

  return (
    <div className="stats__panel ads-page__presence-panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('ads.presenceTitle')}
          <CustomTooltip tooltipText="ads.presenceDesc" addMargin={false} />
        </div>
        {periodFilterItems.length > 0 && <FilterBox items={periodFilterItems} />}
      </div>
      <div className="stats-box stats-box--chart">
        <div className="stats-box__content">
          {/* Top-right corner of the card (absolute, like the Sentiment chart's switch), sharing the
              row with LineChart's legend — the Overview chart-header layout. No inner chart title: the
              active segment already names the view and the period control names the unit. Hidden while
              the panel shows no chart (no black controls over an empty state). */}
          {!isLoading && !isError && !hasNoAds && (
            <ViewSwitch options={VIEWS} value={view} onChange={setView} />
          )}
          <div className="ads-page__chart-body" key={view}>
            {isLoading ? (
              // The frameless chart placeholder (legend bar + canvas block) — the framed
              // `skeleton--table` is for TABLE sections, where it replaces the card; inside
              // `.stats-box--chart` it would draw a frame within the card's frame.
              <Skeleton className="skeleton--chart">
                <span />
              </Skeleton>
            ) : isError ? (
              <AdsErrorPanel onRetry={onRetry} />
            ) : hasNoAds ? (
              <EmptyPanel textKey="ads.emptyTitle" descriptionKey="ads.emptyDesc" />
            ) : view === 'occurrences' ? (
              <LineChart
                firstData={occurrencesMatrix}
                headers={occurrencesMatrix[0].slice(1) as string[]}
                firstColorPalette={[SELECTED_BRAND_COLOR, getFirstSeriesPalette()[0]]}
                minValue={0}
                maxValue={occurrencesAxisMax}
                isMax30={false}
                smooth={true}
                legendType="FLEX_START"
                dateAxisFormatter={labelFormatters.axis}
                dateTooltipFormatter={labelFormatters.tooltip}
              />
            ) : byAdvertiser ? (
              <>
                {/* Same canvas height as LineChart so all views keep one panel size. */}
                <ReactECharts option={byAdvertiser.option} style={{ height: 294, width: '100%' }} notMerge={true} />
                {byAdvertiser.hiddenAds > 0 && (
                  <div className="ads-page__chart-note">
                    {t('ads.byAdvertiserNote', {
                      top: TOP_ADVERTISERS_PER_BUCKET,
                      unit,
                      ads: byAdvertiser.hiddenAds,
                    })}
                  </div>
                )}
              </>
            ) : (
              <EmptyPanel textKey="ads.emptyTitle" descriptionKey="ads.emptyDesc" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdPresenceSection;
